import React from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faSpinner,
	faPencilAlt,
	faCalendarCheck,
	faMapMarkerAlt,
	faClock,
	faCalendarAlt,
	faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import "moment-timezone";

// Time zone options
const timeZoneOptions = [
	{ value: "America/New_York", label: "Eastern Time (America/New_York)" },
	{ value: "America/Chicago", label: "Central Time (America/Chicago)" },
	{ value: "America/Denver", label: "Mountain Time (America/Denver)" },
	{ value: "America/Los_Angeles", label: "Pacific Time (America/Los_Angeles)" },
	{ value: "America/Anchorage", label: "Alaska Time (America/Anchorage)" },
	{ value: "Pacific/Honolulu", label: "Hawaii Time (Pacific/Honolulu)" },
	{ value: "UTC", label: "Coordinated Universal Time (UTC)" },
];

// Address Type ID
const DEFAULT_ADDRESS_TYPE_ID = 2;

// Update Event Form Component
const UpdateEventForm = ({
	detailedEventData,
	editFormData,
	handleEditFormChange,
	handleUpdateSubmit,
	updateError,
	updateLoading,
	setIsEditing,
	serviceTypes = [],
}) => {
	const originalAddress = detailedEventData.addresses?.[0];
	const originalAddressTypeID =
		originalAddress?.addressTypeID || DEFAULT_ADDRESS_TYPE_ID;
	
	const selectedServiceTypeIDs = Array.isArray(editFormData.serviceTypeIDs)
		? editFormData.serviceTypeIDs
		: [];
	
	const handleServiceTypeToggle = (serviceTypeID) => {
		const idStr = String(serviceTypeID);

		const next = selectedServiceTypeIDs.includes(idStr)
			? selectedServiceTypeIDs.filter((x) => x !== idStr)
			: [...selectedServiceTypeIDs, idStr];

		// Use your existing handler shape by faking an event
		handleEditFormChange({
			target: { name: "serviceTypeIDs", value: next },
		});
	};

	return (
		<Form onSubmit={handleUpdateSubmit}>
			{updateError && <Alert variant="danger">{updateError}</Alert>}

			<h4 className="mb-3 text-primary">
				<FontAwesomeIcon icon={faPencilAlt} className="me-2" />
				Event Details
			</h4>
			<div className="row g-3 mb-4">
				<div className="col-md-6">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editTitle"
							name="title"
							value={editFormData.title}
							onChange={handleEditFormChange}
							placeholder="Event Title"
							required
						/>
						<Form.Label htmlFor="editTitle">Title</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="form-floating">
						<Form.Control
							as="textarea"
							rows={3}
							id="editDescription"
							name="description"
							value={editFormData.description}
							onChange={handleEditFormChange}
							placeholder="Detailed Description"
							style={{ minHeight: "100px" }}
						/>
						<Form.Label htmlFor="editDescription">Description</Form.Label>
					</Form.Group>
				</div>
			</div>

			{/*Services at Event */}
			<h4 className="mb-3 text-primary">
				<FontAwesomeIcon icon={faListCheck} className="me-2" />
				Services Offered
			</h4>

			<div className="row g-3 mb-4">
				<div className="col-12">
					{serviceTypes.length === 0 ? (
						<Alert variant="warning" className="mb-0">
							No service types are available yet.
						</Alert>
					) : (
						<div className="border rounded p-3 bg-light">
							<div className="d-flex flex-wrap gap-3">
								{serviceTypes.map((st) => {
									const idStr = String(st.serviceTypeID);
									const checked = selectedServiceTypeIDs.includes(idStr);

									return (
										<Form.Check
											key={idStr}
											type="checkbox"
											id={`svc-${idStr}`}
											label={st.name}
											checked={checked}
											onChange={() => handleServiceTypeToggle(idStr)}
										/>
									);
								})}
							</div>

							<Form.Text className="text-muted d-block mt-2">
								Select all services that will be available at this event.
							</Form.Text>
						</div>
					)}
				</div>
			</div>

			<h4 className="mb-3 text-primary">
				<FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
				Date & Time
			</h4>

			<div className="row g-3 mb-4">
				<div className="col-md-3">
					<Form.Group className="form-floating">
						<Form.Control
							type="date"
							id="editEffectiveFrom"
							name="effectiveFrom"
							value={editFormData.effectiveFrom}
							onChange={handleEditFormChange}
							required
						/>
						<Form.Label htmlFor="editEffectiveFrom">Start Date</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-3">
					<Form.Group className="form-floating">
						<Form.Control
							type="date"
							id="editEffectiveTo"
							name="effectiveTo"
							value={editFormData.effectiveTo}
							onChange={handleEditFormChange}
							required
						/>
						<Form.Label htmlFor="editEffectiveTo">End Date</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-3">
					<Form.Group className="form-floating">
						<Form.Control
							type="time"
							id="editStartTime"
							name="startTime"
							value={editFormData.startTime}
							onChange={handleEditFormChange}
						/>
						<Form.Label htmlFor="editStartTime">
							<FontAwesomeIcon icon={faClock} className="me-1" />
							Start Time
						</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-3">
					<Form.Group className="form-floating">
						<Form.Control
							type="time"
							id="editEndTime"
							name="endTime"
							value={editFormData.endTime}
							onChange={handleEditFormChange}
						/>
						<Form.Label htmlFor="editEndTime">End Time</Form.Label>
					</Form.Group>
				</div>
			</div>

			<div className="row g-3 mb-4">
				<div className="col-md-4">
					<Form.Group className="form-floating">
						<Form.Select
							className="form-select form-select-floating-fix"
							id="editTimeZone"
							name="timeZone"
							value={editFormData.timeZone}
							onChange={handleEditFormChange}
							required
						>
							<option value="">Select Time Zone</option>
							{timeZoneOptions.map((tz) => (
								<option key={tz.value} value={tz.value}>
									{tz.label}
								</option>
							))}
						</Form.Select>
						<Form.Label htmlFor="editTimeZone">Time Zone</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="form-floating">
						<Form.Control
							type="date"
							id="editPushDate"
							name="bulletinBoardPushDate"
							value={editFormData.bulletinBoardPushDate}
							onChange={handleEditFormChange}
							placeholder="YYYY-MM-DD"
						/>
						<Form.Label htmlFor="editPushDate">
							Bulletin Board Push Date
						</Form.Label>
						<Form.Text className="text-muted">
							(Optional: Date when event appears on bulletin board)
						</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-4"></div>
			</div>

			<h4 className="mb-3 text-primary">
				<FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
				Location Information
			</h4>
			<div className="row g-3 mb-4">
				<div className="col-md-6">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editLocationName"
							name="locationName"
							value={editFormData.locationName}
							onChange={handleEditFormChange}
							placeholder="e.g., Central Park Pavilion"
							required
						/>
						<Form.Label htmlFor="editLocationName">Location Name</Form.Label>
						<Form.Text className="text-muted">
							(Required: Location name such as "First Baptist")
						</Form.Text>
					</Form.Group>
				</div>
				<div className="col-md-6">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editAddressLine1"
							name="addressLine1"
							value={editFormData.addressLine1}
							onChange={handleEditFormChange}
							placeholder="Address Line 1"
							required
						/>
						<Form.Label htmlFor="editAddressLine1">
							Street Address (Line 1)
						</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editCity"
							name="city"
							value={editFormData.city}
							onChange={handleEditFormChange}
							placeholder="City"
							required
						/>
						<Form.Label htmlFor="editCity">City</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editState"
							name="state"
							value={editFormData.state}
							onChange={handleEditFormChange}
							placeholder="State Abbreviation (e.g., IL)"
							maxLength={2}
							required
						/>
						<Form.Label htmlFor="editState">State (e.g., IL)</Form.Label>
					</Form.Group>
				</div>
				<div className="col-md-4">
					<Form.Group className="form-floating">
						<Form.Control
							type="text"
							id="editZip"
							name="zip"
							value={editFormData.zip}
							onChange={handleEditFormChange}
							placeholder="Zip Code"
							required
						/>
						<Form.Label htmlFor="editZip">Zip Code</Form.Label>
					</Form.Group>
				</div>
			</div>

			

			<input type="hidden" name="addressTypeID" value={originalAddressTypeID} />

			<div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
				<Button
					variant="secondary"
					onClick={() => setIsEditing(false)}
					disabled={updateLoading}
				>
					Cancel
				</Button>
				<Button variant="primary" type="submit" disabled={updateLoading}>
					{updateLoading ? (
						<>
							<Spinner animation="border" size="sm" className="me-2" />
							Updating...
						</>
					) : (
						<>
							<i className="bi bi-save me-2"></i>Save Changes
						</>
					)}
				</Button>
			</div>
		</Form>
	);
};

// EventDetailsModal Component
const EventDetailsModal = ({
	show,
	onHide,
	isEditing,
	setIsEditing,
	detailedEventData,
	detailLoading,
	editFormData,
	handleEditFormChange,
	handleUpdateSubmit,
	updateError,
	updateLoading,
	authLoading,
	userRole,
	userOrgID,
	canEditEvent,
	handleShowDeleteConfirm,
	handleEditEvent,
	getImageUrl,
	serviceTypes = [],
}) => {
	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			size="lg"
			style={{ fontFamily: "var(--bs-body-font-family)" }}
		>
			<Modal.Header closeButton className="text-white">
				<Modal.Title className="d-flex align-items-center flex-grow-1">
					<h3 className="mb-0">Edit Event Details</h3>
					{authLoading ? (
						<div
							className="spinner-border spinner-border-sm ms-3 text-white"
							role="status"
						>
							<span className="visually-hidden">Loading permissions...</span>
						</div>
					) : (
						canEditEvent(userRole, userOrgID, detailedEventData) &&
						isEditing
					)}
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{isEditing ? (
					<UpdateEventForm
						detailedEventData={detailedEventData}
						editFormData={editFormData}
						handleEditFormChange={handleEditFormChange}
						handleUpdateSubmit={handleUpdateSubmit}
						updateError={updateError}
						updateLoading={updateLoading}
					setIsEditing={setIsEditing}
					serviceTypes={serviceTypes}
				/>
				) : detailLoading ? (
					<div className="text-center py-5">
						<FontAwesomeIcon
							icon={faSpinner}
							spin
							size="2x"
							className="text-primary"
						/>
						<p className="mt-2">Fetching event details...</p>
					</div>
				) : detailedEventData ? (
					<div className="row">
						<div className="col-12 mb-4">
							<h3 className="text-primary">
								{detailedEventData.title || "No Title"}
							</h3>
						</div>

						<div className="col-lg-6 col-md-6 col-12 mb-3">
							<h5 className="mb-1 text-secondary">
								<i className="bi bi-bank me-2"></i>Hosting Organization
							</h5>
							<p className="lead fw-medium mb-0">
								{detailedEventData.orgName || "N/A"}
							</p>
						</div>

						<div className="col-lg-6 col-md-6 col-12 mb-3">
							<h5 className="mb-1 text-secondary">
								<i className="bi bi-geo-alt-fill me-2"></i>Location
							</h5>
							{(() => {
								const address =
									detailedEventData.addresses &&
									detailedEventData.addresses.length > 0
										? detailedEventData.addresses[0]
										: null;

								const formattedLocation = address
									? `${address.locationName ? address.locationName + ", " : ""}${address.addressLine1}, ${address.city}, ${address.state} ${address.zip}`
									: "Not Specified";

								return ( 
									
									<p className="lead fw-medium" style={{ whiteSpace: "pre-wrap" }}>
										<a
											href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedLocation)}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-underline-on-hover text-primary"
											>{formattedLocation}   <i className="bi bi-box-arrow-up-right"></i>
										</a>
									</p>
								);
							})()}
						</div>

						<div className="col-lg-6 col-md-6 col-12 mb-3">
							<h5 className="mb-1 text-secondary">
								<FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
								Starts (Your Local Time)
							</h5>
							<p className="lead fw-medium text-success">
								{(() => {
									const schedule =
										detailedEventData.schedule &&
										detailedEventData.schedule.length > 0
											? detailedEventData.schedule[0]
											: null;
									if (!schedule) return "N/A";

									const eventStartUTC = moment.tz(
										`${schedule.effectiveFrom} ${schedule.startTime}`,
										"YYYY-MM-DD HH:mm:ss",
										schedule.timeZone,
									);

									const localStart = eventStartUTC.local();
									const userTimeZoneName = moment.tz.guess();

									const formattedDate = localStart.format("MMM D, YYYY");
									const formattedTime = localStart.format("h:mm A");

									return `${formattedDate} at ${formattedTime} (${userTimeZoneName})`;
								})()}
							</p>
						</div>

						<div className="col-lg-6 col-md-6 col-12 mb-3">
							<h5 className="mb-1 text-secondary">
								<i className="bi bi-calendar-x-fill me-2"></i>
								Ends (Your Local Time)
							</h5>
							<p className="lead fw-medium text-danger">
								{(() => {
									const schedule =
										detailedEventData.schedule &&
										detailedEventData.schedule.length > 0
											? detailedEventData.schedule[0]
											: null;

									if (!schedule) return "N/A";

									const eventEndUTC = moment.tz(
										`${schedule.effectiveTo} ${schedule.endTime}`,
										"YYYY-MM-DD HH:mm:ss",
										schedule.timeZone,
									);

									const localEnd = eventEndUTC.local();
									const userTimeZoneName = moment.tz.guess();

									const formattedDate = localEnd.format("MMM D, YYYY");
									const formattedTime = localEnd.format("h:mm A");

									return `${formattedDate} at ${formattedTime} (${userTimeZoneName})`;
								})()}
							</p>
						</div>

						<div className="col-12 mt-3 pt-3 border-top">
							<h5 className="mb-2">
								<i className="bi bi-card-checklist me-2"></i>Services Offered
							</h5>
							{detailedEventData.serviceTypes &&
							detailedEventData.serviceTypes.length > 0 ? (
								<div className="d-flex flex-wrap gap-2">
									{detailedEventData.serviceTypes.map((service) => (
										<span
											key={service.serviceTypeID}
											className="badge bg-primary fs-6 px-3 py-2"
										>
											{service.name}
										</span>
									))}
								</div>
							) : (
								<p className="text-muted fst-italic">
									No services specified for this event.
								</p>
							)}
						</div>

						<div className="col-12 mt-3 pt-3 border-top">
							<h5 className="mb-2">
								<i className="bi bi-file-text-fill me-2"></i>Description
							</h5>
							<p className="text-wrap lead fw-medium">
								{detailedEventData.description || (
									<em>No description provided for this event.</em>
								)}
							</p>
						</div>
					</div>
				) : (
					<Alert variant="info" className="text-center">
						Select an event to see its details.
					</Alert>
				)}
			</Modal.Body>
			{detailedEventData &&
				!isEditing &&
				canEditEvent(userRole, userOrgID, detailedEventData) && (
					<Modal.Footer>
						<Button variant="danger" onClick={handleShowDeleteConfirm}>
							<i className="bi bi-trash-fill me-2"></i>Delete
						</Button>
						<Button
							variant="primary"
							onClick={handleEditEvent}
							className="ms-auto"
						>
							<i className="bi bi-pencil-fill me-2"></i>Edit
						</Button>
					</Modal.Footer>
				)}
		</Modal>
	);
};

export default EventDetailsModal;
